// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-explicit-any, react/prop-types */

import React, { createContext, useCallback, useContext, useReducer } from "react";

export type ChartType = "box-jitter" | "box" | "bar" | "table" | "line";

interface GraphState {
	selectedFeatures: GeoJSON.Feature<any, any>[];
	plotIds: string[];
	yAxis: string;
	xAxis: string;
	xOrder: string[];
	chartType: ChartType;
}

interface GraphFunctions {
	updateSelectedFeatures: (features: GeoJSON.Feature<any, any>[]) => void;
	updatePlotIds: (ids: string[]) => void;
	updateAxis: (axis: { x?: string; y?: string; xOrder?: string[] }) => void;
	updateChartType: (type: ChartType) => void;
}

type GraphAction =
	| { type: "select features"; features: GeoJSON.Feature<any, any>[] }
	| { type: "set plot ids"; ids: string[] }
	| { type: "set axis"; axisInfo: { yAxis?: string; xAxis?: string; xOrder?: string[] } }
	| { type: "set chart type"; chartType: ChartType };

const graphReducer = (state: GraphState, action: GraphAction) => {
	switch (action.type) {
		case "select features":
			return { ...state, selectedFeatures: action.features };
		case "set plot ids":
			return { ...state, plotIds: action.ids };
		case "set axis":
			return {
				...state,
				xAxis: action.axisInfo.xAxis ?? state.xAxis,
				yAxis: action.axisInfo.yAxis ?? state.yAxis,
				xOrder: action.axisInfo.xOrder ?? state.xOrder,
			};
		case "set chart type":
			return {
				...state,
				chartType: action.chartType,
			};
		default:
			return state;
	}
};

const GraphContext = createContext<GraphState | null>(null);
const GraphDispatchContext = createContext<GraphFunctions | null>(null);

// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
const GraphProvider = ({ children }) => {
	const [graph, dispatch] = useReducer(graphReducer, {
		selectedFeatures: [],
		plotIds: [],
		yAxis: "",
		xAxis: "",
		xOrder: [],
		chartType: "box-jitter",
	});

	const updateSelectedFeatures = useCallback(
		(features: GeoJSON.Feature<any, any>[]) => dispatch({ type: "select features", features }),
		[],
	);

	const updatePlotIds = useCallback((ids: string[]) => dispatch({ type: "set plot ids", ids }), []);

	const updateAxis = useCallback(
		(axis: { x?: string; y?: string; xOrder?: string[] }) =>
			dispatch({ type: "set axis", axisInfo: { xAxis: axis.x, yAxis: axis.y, xOrder: axis.xOrder } }),
		[],
	);

	const updateChartType = useCallback((type: ChartType) => dispatch({ type: "set chart type", chartType: type }), []);

	return (
		<GraphContext.Provider value={graph}>
			<GraphDispatchContext.Provider value={{ updateSelectedFeatures, updatePlotIds, updateAxis, updateChartType }}>
				{children}
			</GraphDispatchContext.Provider>
		</GraphContext.Provider>
	);
};

const useGraph = () => {
	const context = useContext(GraphContext);

	if (context === null) {
		throw new Error("GraphContext is not provided");
	}

	return context;
};

const useGraphDispatch = () => {
	const context = useContext(GraphDispatchContext);

	if (context === null) {
		throw new Error("GraphDispatchContext is not provided");
	}

	return context;
};

export { GraphProvider, useGraph, useGraphDispatch };
